import React from "react";
import styled, { keyframes } from "styled-components";
import { $styles, IStyles } from "./$styles";

export interface IRevealSectionProps {
  isReady: boolean;
  skeleton?: React.ReactNode;
  children: React.ReactNode;
}

const RevealSection: React.FC<IRevealSectionProps & IStyles> = ({
  isReady,
  skeleton,
  children,
  styles,
}): JSX.Element => {
  const [prevReady, setPrevReady] = React.useState(isReady);
  const [isSkeletonLeaving, setIsSkeletonLeaving] = React.useState(false);

  if (prevReady !== isReady) {
    setPrevReady(isReady);
    if (!prevReady && isReady && skeleton) {
      setIsSkeletonLeaving(true);
    }
  }

  return (
    <Root styles={styles}>
      {!isReady && skeleton}
      {isSkeletonLeaving && skeleton && (
        <SkeletonLayer
          onAnimationEnd={(event) => {
            if (event.target === event.currentTarget) {
              setIsSkeletonLeaving(false);
            }
          }}
        >
          {skeleton}
        </SkeletonLayer>
      )}
      <Content
        $isReady={isReady}
        $hasSkeleton={Boolean(skeleton)}
        aria-hidden={!isReady ? true : undefined}
      >
        {children}
      </Content>
    </Root>
  );
};

export default RevealSection;

const Root = styled.div<IStyles>`
  position: relative;
  ${$styles}
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const SkeletonLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  pointer-events: none;
  animation: ${fadeOut} 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const Content = styled.div<{ $isReady: boolean; $hasSkeleton: boolean }>`
  opacity: ${({ $isReady }) => ($isReady ? 1 : 0)};
  transform: ${({ $isReady }) => ($isReady ? "none" : "translateY(8px)")};
  pointer-events: ${({ $isReady }) => ($isReady ? "auto" : "none")};
  transition:
    opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  ${({ $hasSkeleton, $isReady }) => {
    if ($hasSkeleton && !$isReady) {
      return `
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
      `;
    }
    return `
      position: static;
    `;
  }}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none;
  }
`;
